// Import 3rd-party dependencies
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { Observable, combineLatest, of } from 'rxjs';

// Import project-specific dependencies
import { ApiService } from '@app/services/api.service';
import { INotificationInfo } from '@app/services/notifications/notification.service';
import { environment } from '@app/environments/environment';
import { DXCHUB_NOTIFICATION_SERVICE_CONSTS } from '@app/config/constants';
import { IAnnouncements } from '@app/services/admin/announcement-maintenance/announcement-maintenance.service';
import { PeopleService } from '@app/services/people/people.service';
import { UserProfileService } from '@app/services/user-profile/user-profile.service';

@Injectable({
  providedIn: 'root'
})
export class DxchubNotificationService extends ApiService {
  private ADMIN_API_URL = environment.DEPS.ADMIN_API.URL;
  consts = DXCHUB_NOTIFICATION_SERVICE_CONSTS;

  constructor(
    httpClient: HttpClient,
    private peopleService: PeopleService,
    private profileService: UserProfileService
  ) {
    super(httpClient);
  }

  private readonly coreId$ = this.peopleService.currentUser$.pipe(
    map((user) => user?.employeeId || ''),
    shareReplay()
  );
  private readonly manager_coreId$ = this.peopleService.managerInfo$.pipe(
    map((user) => user?.employeeId || ''),
    shareReplay()
  );
  private readonly department$ = this.peopleService.currentUser$.pipe(
    map((user) => user?.department || ''),
    shareReplay()
  );
  private readonly employee_region$ = this.coreId$.pipe(
    switchMap((coreId) => this.profileService.getDdProfile(coreId)),
    map((ddProfile) => ddProfile?.CORPORATE_REGION || ''),
    shareReplay()
  );
  private readonly employee_isManager$ = this.coreId$.pipe(
    switchMap((coreId) => this.profileService.getDdProfile(coreId)),
    map((ddProfile) => (ddProfile?.IS_MGR_IND === '1' ? true : false)),
    shareReplay()
  );

  public getAllAnnouncements(): Observable<IAnnouncements[]> {
    return combineLatest([
      this.manager_coreId$,
      this.department$,
      this.employee_region$,
      this.employee_isManager$
    ]).pipe(
      switchMap(([managerID, department, region, isManager]) =>
        this.get<IAnnouncements[]>(
          `${this.ADMIN_API_URL}/me/announcements/filterby?managerCoreID=${managerID}&department=${department}&region=${region}&isManager=true`
        ).pipe(map((list) => (!isManager ? list.filter((item) => !item.isManager) : list)))
      ),
      map((list) => {
        const disabledNotifList = JSON.parse(localStorage.getItem('DisabledNotifList')) || [];
        return list.filter((item) => disabledNotifList.indexOf(item.announcementID) === -1);
      }),
      map((list) =>
        list.filter((item) => new Date(item.startDate) <= new Date() && new Date() <= new Date(item.endDate))
      ),
      map((list) => list.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()))
    );
  }

  public getNotifications(): Observable<INotificationInfo[]> {
    return this.getAllAnnouncements().pipe(
      map((nlist) => nlist.filter((item) => !item.isTopBanner)),
      map((notifications) => {
        let notificationList: INotificationInfo[] = [];
        notifications.forEach((element) => {
          notificationList.push({
            announcementID: element.announcementID,
            from: element.fullName,
            title: this.consts.TITLE,
            link: element.link,
            message: element.message,
            count: 1,
            actionNeeded: false,
            due: new Date(element.startDate),
            origObj: element
          });
        });
        return notificationList;
      })
    );
  }
}
